import { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import Header from "../../components/Header";
import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import HallNoticeModal from "../../components/HallNoticeModal";
import AgencySafetyModal from "../../components/AgencySafetyModal";
import Gg88DecryptFlow from "../../components/Gg88DecryptFlow";
import { getAssetUrl } from "../../utils/assetUrl";
import "./HomePage.css";

/** XX88 ở vị trí cũ của GG88 (index 1), GG88 mặc định ở giữa (index 2). */
const LOBBY_CARDS = [
  {
    id: "8kbet",
    title: "8KBET",
    logo: "/assets/logo-8kbet.png",
  },
  {
    id: "xx88",
    title: "XX88",
    logo: "/assets/logo-xx88.png",
  },
  {
    id: "gg88",
    title: "GG88",
    logo: "/assets/logo-gg88.png",
  },
  {
    id: "c168",
    title: "C168",
    logo: "/assets/logo-c168.png",
  },
  {
    id: "open88",
    title: "OPEN88",
    logo: "/assets/logo-open88.png",
  },
] as const;

const PLAYABLE_LOBBY_ID = "gg88";

type LobbyId = (typeof LOBBY_CARDS)[number]["id"];

function randomPercent(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function buildLobbyRates(): Record<LobbyId, string> {
  const rates = {} as Record<LobbyId, string>;
  for (const card of LOBBY_CARDS) {
    const pct =
      card.id === PLAYABLE_LOBBY_ID
        ? randomPercent(90, 95)
        : randomPercent(28, 38);
    rates[card.id] = `${pct}%`;
  }
  return rates;
}

const HomePage = () => {
  const [isShowLogout, setIsShowLogout] = useState(false);
  const [selectedLobbyId, setSelectedLobbyId] = useState<LobbyId>(PLAYABLE_LOBBY_ID);
  const [lobbyRates, setLobbyRates] = useState(buildLobbyRates);
  const [noticeLobbyName, setNoticeLobbyName] = useState<string | null>(null);
  const [isAgencySafetyOpen, setIsAgencySafetyOpen] = useState(false);
  const [isDecryptFlowOpen, setIsDecryptFlowOpen] = useState(false);
  const [trackOffset, setTrackOffset] = useState(0);
  const [carouselDesktop, setCarouselDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 901px)").matches
  );

  const cardsViewportRef = useRef<HTMLDivElement>(null);
  const cardsTrackRef = useRef<HTMLDivElement>(null);

  const activeLobbyIndex = Math.max(
    0,
    LOBBY_CARDS.findIndex((card) => card.id === selectedLobbyId)
  );

  const updateTrackOffset = useCallback(() => {
    const viewport = cardsViewportRef.current;
    const track = cardsTrackRef.current;
    if (!viewport || !track || !carouselDesktop) {
      setTrackOffset(0);
      return;
    }

    const activeCard = track.querySelector<HTMLElement>(
      `#lobby-card-${selectedLobbyId}`
    );
    if (!activeCard) return;

    const cardCenter = activeCard.offsetLeft + activeCard.offsetWidth / 2;
    const viewportCenter = viewport.clientWidth / 2;
    setTrackOffset(viewportCenter - cardCenter);
  }, [carouselDesktop, selectedLobbyId]);

  const goToLobbyIndex = useCallback(
    (index: number) => {
      const normalized =
        ((index % LOBBY_CARDS.length) + LOBBY_CARDS.length) %
        LOBBY_CARDS.length;
      const card = LOBBY_CARDS[normalized];
      setSelectedLobbyId(card.id);

      if (!carouselDesktop) {
        requestAnimationFrame(() => {
          document.getElementById(`lobby-card-${card.id}`)?.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        });
      }
    },
    [carouselDesktop]
  );

  const goLobbyBack = () => goToLobbyIndex(activeLobbyIndex - 1);
  const goLobbyNext = () => goToLobbyIndex(activeLobbyIndex + 1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 901px)");
    const onChange = () => setCarouselDesktop(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useLayoutEffect(() => {
    updateTrackOffset();
    const track = cardsTrackRef.current;
    if (!track) return undefined;

    const onTransitionEnd = (event: TransitionEvent) => {
      if (
        event.target === track &&
        (event.propertyName === "transform" || event.propertyName === "width")
      ) {
        updateTrackOffset();
      }
    };

    track.addEventListener("transitionend", onTransitionEnd);
    const resizeObserver = new ResizeObserver(() => updateTrackOffset());
    if (cardsViewportRef.current) {
      resizeObserver.observe(cardsViewportRef.current);
    }
    resizeObserver.observe(track);

    return () => {
      track.removeEventListener("transitionend", onTransitionEnd);
      resizeObserver.disconnect();
    };
  }, [updateTrackOffset, activeLobbyIndex, carouselDesktop]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setLobbyRates(buildLobbyRates());
    }, 10000);
    return () => window.clearInterval(timer);
  }, []);

  const onLobbyClick = (card: (typeof LOBBY_CARDS)[number]) => {
    if (card.id !== selectedLobbyId) {
      goToLobbyIndex(LOBBY_CARDS.findIndex((item) => item.id === card.id));
    }

    if (card.id !== PLAYABLE_LOBBY_ID) {
      setNoticeLobbyName(card.title);
      return;
    }

    setSelectedLobbyId(card.id);
    setIsAgencySafetyOpen(true);
  };

  return (
    <div className="x88-hub">
      <div className="x88-hub__header-zone">
        <Header setIsShowLogout={() => setIsShowLogout(true)} />
      </div>

      <main className="x88-hub__main">
        <section className="x88-hub__hero" aria-label="Chọn sảnh game">
          <h1 className="x88-hub__hero-title">CHỌN SẢNH GAME</h1>
        </section>

        <section className="x88-hub__cards-zone" aria-label="Chọn game">
          <div className="x88-hub__cards-viewport" ref={cardsViewportRef}>
            <div
              ref={cardsTrackRef}
              className="x88-hub__cards-track"
              style={
                carouselDesktop
                  ? { transform: `translateX(${trackOffset}px)` }
                  : undefined
              }
            >
            {LOBBY_CARDS.map((card) => {
              const isSelected = card.id === selectedLobbyId;
              return (
                <button
                  key={card.id}
                  id={`lobby-card-${card.id}`}
                  type="button"
                  className={`x88-hub__card ${
                    isSelected ? "x88-hub__card--selected" : ""
                  } x88-hub__card--${card.id}`}
                  onClick={() => onLobbyClick(card)}
                >
                  <div className="x88-hub__card-shell">
                    <div className="x88-hub__card-image-box">
                      <img
                        className="x88-hub__card-logo-img"
                        src={getAssetUrl(card.logo)}
                        alt={card.title}
                      />
                    </div>
                    <span className="x88-hub__card-title">{card.title}</span>
                    <div className="x88-hub__card-label">
                      <span>{lobbyRates[card.id]}</span>
                    </div>
                  </div>
                </button>
              );
            })}
            </div>
          </div>

          <div className="x88-hub__lobby-actions" aria-label="Điều hướng sảnh">
            <div className="x88-hub__lobby-nav">
              <button
                type="button"
                className="x88-hub__nav-btn x88-hub__nav-btn--back"
                onClick={goLobbyBack}
              >
                <span className="x88-hub__nav-btn-arrows" aria-hidden>
                  ««
                </span>
                BACK
              </button>

              <div
                className="x88-hub__lobby-dots"
                role="tablist"
                aria-label="Vị trí sảnh"
              >
                {LOBBY_CARDS.map((card, index) => (
                  <button
                    key={card.id}
                    type="button"
                    role="tab"
                    aria-selected={index === activeLobbyIndex}
                    aria-label={`Sảnh ${card.title}`}
                    className={`x88-hub__lobby-dot ${
                      index === activeLobbyIndex ? "x88-hub__lobby-dot--active" : ""
                    }`}
                    onClick={() => goToLobbyIndex(index)}
                  />
                ))}
              </div>

              <button
                type="button"
                className="x88-hub__nav-btn x88-hub__nav-btn--next"
                onClick={goLobbyNext}
              >
                NEXT
                <span className="x88-hub__nav-btn-arrows" aria-hidden>
                  »»
                </span>
              </button>
            </div>

            <button
              type="button"
              className="x88-hub__agency-btn"
              onClick={() => setIsDecryptFlowOpen(true)}
            >
              &gt;_XÓA MÃ ẨN_&lt;
            </button>
          </div>
        </section>
      </main>

      <ModalConfirmLogout
        isShowLogout={isShowLogout}
        setIsShowLogout={() => setIsShowLogout(false)}
      />

      <HallNoticeModal
        isOpen={noticeLobbyName != null}
        hallBrand={noticeLobbyName ?? ""}
        onClose={() => setNoticeLobbyName(null)}
      />

      <AgencySafetyModal
        isOpen={isAgencySafetyOpen}
        onClose={() => setIsAgencySafetyOpen(false)}
        onConfirm={() => setIsAgencySafetyOpen(false)}
      />

      <Gg88DecryptFlow
        isOpen={isDecryptFlowOpen}
        onClose={() => setIsDecryptFlowOpen(false)}
      />
    </div>
  );
};

export default HomePage;
